import * as Sentry from '@sentry/tanstackstart-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { onNativeAuthReady, onNativeTokenStored } from '#features/api/utils/nativeAuth';
import { openAppSettings } from '#features/notifications/utils/deviceSettings';
import { leaveVoice } from '#features/voice/model/voice';
import { useVoiceStore, type VoiceRoomInfo } from '#features/voice/model/voiceStore';
import {
  connectNativeVoiceSession,
  disconnectNativeVoiceSession,
  invalidateNativeVoiceSessionAuth,
  isNativeVoiceSessionCandidate,
  requestVoiceMicrophone,
  setNativeVoiceParticipantAudio,
  subscribeNativeVoiceSession,
  syncNativeVoiceSessionAuth,
  useNativeVoiceSession,
} from '#features/voice/utils/nativeVoiceSession';

const connecting = new WeakSet<VoiceRoomInfo>();
const noop = () => {};

/** Observe process-owned media. A WebView reload or provider cleanup must not end the conversation. */
export const useNativeVoiceRoom = () => {
  const queryClient = useQueryClient();
  const room = useVoiceStore((state) => state.room);
  const micDenied = useVoiceStore((state) => state.micDenied);
  const mode = useNativeVoiceSession((state) => state.mode);
  const ready = useNativeVoiceSession((state) => state.ready);
  const snapshot = useNativeVoiceSession((state) => state.snapshot);
  const audioContainerRef = useRef<HTMLDivElement | null>(null);
  const recoverMicOnReturn = useRef(false);

  useEffect(() => {
    if (!isNativeVoiceSessionCandidate()) return;
    let disposed = false;
    let off: (() => void) | undefined;
    let subscription: Promise<() => void> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const sync = async () => {
      try {
        subscription ??= subscribeNativeVoiceSession().catch((error: unknown) => {
          subscription = undefined;
          throw error;
        });
        const remove = await subscription;
        if (disposed) {
          remove();
          return;
        }
        off = remove;
        await syncNativeVoiceSessionAuth();
      } catch {
        if (!disposed) retry = setTimeout(() => void sync(), 2_000);
      }
    };
    void sync();
    const offAuth = onNativeAuthReady(() => void sync());
    const offToken = onNativeTokenStored(invalidateNativeVoiceSessionAuth);
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      void sync().then(() => {
        if (!disposed && recoverMicOnReturn.current) {
          recoverMicOnReturn.current = false;
          return requestVoiceMicrophone(true);
        }
        return undefined;
      });
    };
    document.addEventListener('visibilitychange', refresh);
    return () => {
      disposed = true;
      clearTimeout(retry);
      off?.();
      offAuth();
      offToken();
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  useEffect(() => {
    if (mode !== 'native' || !ready || !room || !room.livekitUrl || !room.token) return;
    const active = useNativeVoiceSession.getState().snapshot;
    if (active?.sessionId === room.sessionId && active.phase !== 'idle' && active.phase !== 'ended')
      return;
    if (connecting.has(room)) return;
    connecting.add(room);
    void connectNativeVoiceSession(room)
      .catch((error: unknown) => {
        if (useVoiceStore.getState().room !== room) return;
        Sentry.captureException(error, {
          tags: { scope: 'nativeVoiceSession', operation: 'connect' },
        });
        const store = useVoiceStore.getState();
        store.reset();
        store.setError('voice.connect.failed', room.sessionId);
      })
      .finally(() => connecting.delete(room));
  }, [mode, ready, room]);

  useEffect(() => {
    if (mode !== 'native') return;
    // Session end, removal and call arbitration can reset the store outside the Leave button.
    return useVoiceStore.subscribe((current, previous) => {
      if (previous.room && !current.room) {
        void disconnectNativeVoiceSession(previous.room.sessionId).catch(() => undefined);
      }
    });
  }, [mode]);

  const leave = useCallback(() => leaveVoice(queryClient), [queryClient]);
  const requestMicAccess = useCallback(async () => {
    if (useVoiceStore.getState().micDenied) {
      recoverMicOnReturn.current = true;
      await openAppSettings();
      return false;
    }
    return requestVoiceMicrophone(true);
  }, []);
  const setParticipantVolume = useCallback((userId: string, volume: number) => {
    void setNativeVoiceParticipantAudio(userId, { volume });
  }, []);
  const setParticipantMuted = useCallback((userId: string, muted: boolean) => {
    void setNativeVoiceParticipantAudio(userId, { muted });
  }, []);
  const localSpeaking =
    snapshot?.sessionId === room?.sessionId &&
    snapshot?.members.some((member) => member.userId === snapshot.authUserId && member.speaking);

  return {
    audioContainerRef,
    micLevel: { level: localSpeaking ? 1 : 0, listening: false, heard: false, failed: false },
    microphoneTrack: null as MediaStreamTrack | null,
    micDenied,
    requestMicAccess,
    audioBlocked: false,
    unblockAudio: async () => {},
    leave,
    setParticipantVolume,
    setParticipantMuted,
    // The existing iOS DeviceSettings picker owns native routes without another media capture.
    listDevices: async (_kind: 'audioinput' | 'audiooutput') => [] as MediaDeviceInfo[],
    switchDevice: async (_kind: 'audioinput' | 'audiooutput', _deviceId: string) => false,
    activeDeviceIds: {} as Partial<Record<'audioinput' | 'audiooutput', string>>,
    refreshDeviceIds: noop,
  };
};
